import { useContext, useEffect, useMemo, useRef } from "react";
import { Box, CircularProgress, Grid } from "@mui/material";
import QRCode from "react-fancy-qrcode";
import LogoRupin from "../../../assets/icons/Logo Rupin.svg";
import TitleAndActionBox from "../TitleAndActionBox";
import { LanguageContext } from "../../../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { ParagraphText, UBButton } from "../../../Libraries";
import formStyles from "../../container/SaleExpense/SaleExpenseForm/styles.module.css";
import { CopyIconNew, GreenCheck, RupinFavIcon } from "../../../assets/icons/icon";
import { useWalletContext } from "../../../context/WalletContext";
import { useSnackbar } from "../../../context/SnackbarContext";
import useQrCodeActions from "./useQrCodeActions";
import styles from "./styles.module.css";

export const SHOW_QR_ACTIONS = {
  PRINT: "print",
  DOWNLOAD: "download",
  SHARE: "share",
};

export const ALL_SHOW_QR_ACTIONS = Object.values(SHOW_QR_ACTIONS);

const OUTLINE_BTN_CLASS = `${styles.outlineBtn} ubButtonStyle ubSecondaryButton textUppercase`;
const PRIMARY_BTN_CLASS = `${styles.primaryBtn} ubButtonStyle ubPrimaryButton textUppercase`;
const OUTLINE_BTN_CLASSTWO = `${styles.outlineBtnTwo} ubButtonStyle ubSecondaryButton textUppercase`;
const ShowQR = ({
  raastDetail: raastDetailProp,
  title,
  onBack,
  showPageLayout = true,
  analyticsPrefix = "w_share_qr",
  className = "",
  actionButtons = [],
}) => {
  const [I18n] = useContext(LanguageContext);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { walletData, walletDataIsLoading } = useWalletContext();
  const isEmiVerified = walletData?.account_info?.emi_onboarding_status === "VERIFIED";
  const raastDetail =
    raastDetailProp || walletData?.account_info?.raast_info?.raast;


  const qrCodeRef = useRef(null);
  const raastDetails = raastDetail?.raast_details;

  const bankDetails = useMemo(
    () => ({
      bankName: raastDetail?.bank_name || "",
      accountTitle: raastDetails?.name || "",
      accountNumber: raastDetails?.account_number || "",
      iban: raastDetails?.iban || "",
      qr_share_key : raastDetail?.raast_details?.qr_share_key
    }),
    [
      raastDetail?.bank_name,
      raastDetails?.account_number,
      raastDetails?.iban,
      raastDetails?.name,
    ],
  );

  const {
    isQrImageCopied,
    isIbanCopied,
    isDownloadLoading,
    handleCopyQrImage,
    handleDownloadQrCode,
    handlePrintQrCode,
    handleCopyIban,
    handleShareDetails,
  } = useQrCodeActions({
    qrCodeRef,
    analyticsPrefix,
    showSnackbar,
    I18n,
    bankDetails,
  });

  const handleBack = onBack || (() => navigate(-1));
  const pageTitle = title || I18n.share_qr || "Share QR";
  const qrValue = raastDetails?.qr_code || "";
  const isQrLoading = walletDataIsLoading && !qrValue;

  const actionButtonConfig = useMemo(
    () => ({
      [SHOW_QR_ACTIONS.PRINT]: {
        title: I18n.print_qr,
        onClick: handlePrintQrCode,
        className: OUTLINE_BTN_CLASS,
        id: "print_qr",
      },
      [SHOW_QR_ACTIONS.DOWNLOAD]: {
        title: I18n.download_qr,
        onClick: handleDownloadQrCode,
        loading: isDownloadLoading,
        className: OUTLINE_BTN_CLASSTWO ,
        id: "download_qr",
      },
      [SHOW_QR_ACTIONS.SHARE]: {
        title: I18n.share_details || "Share details",
        onClick: handleShareDetails,
        className: PRIMARY_BTN_CLASS,
        id: "share_qr_details",
      },
    }),
    [
      I18n.download_qr,
      I18n.print_qr,
      I18n.share_details,
      handleDownloadQrCode,
      handlePrintQrCode,
      handleShareDetails,
      isDownloadLoading,
    ],
  );

  const visibleActionButtons = actionButtons.filter(
    (key) => actionButtonConfig[key],
  );

  const content = (
    <Box className={`${styles.showQrContent} ${className}`}>
      <Box className={styles.qrSection}>
        <Box className={`${styles.qrCodeBox} qrContrastSurface`} ref={qrCodeRef}>
          {isQrLoading ? (
            <CircularProgress size={32} />
          ) : qrValue ? (
            <QRCode
              value={qrValue}
              size={202}
              dotRadius="12%"
              dotScale={1}
              positionRadius={5}
              logo={LogoRupin}
            />
          ) : null}
        </Box>

        {qrValue &&
          (isQrImageCopied ? (
          <Box className={styles.imageCopiedBadge}>
            <GreenCheck width={16} height={16} />
            {I18n.image_copied}
          </Box>
        ) : (
          <button
            type="button"
            className={styles.copyQrImageBtn}
            onClick={handleCopyQrImage}
          >
            <CopyIconNew width={16} height={16} />
            {I18n.copy_qr_image}
          </button>
          ))}
      </Box>

      <Box className={styles.accountCard}>
        <RupinFavIcon />
        <Box className={styles.accountCardInfo}>
          <ParagraphText className={styles.accountName}>
            {bankDetails.accountTitle}
          </ParagraphText>
          <Box className={styles.accountIbanRow}>
            <ParagraphText className={styles.accountIban}>
              {bankDetails.iban}
            </ParagraphText>
            <button
              type="button"
              className={styles.copyIconBtn}
              onClick={handleCopyIban}
              aria-label={I18n.copy}
            >
              <CopyIconNew width={18} height={18} />
              {isIbanCopied ? I18n.copied : ""}
            </button>
          </Box>
        </Box>
      </Box>

      {visibleActionButtons.length > 0 && (
        <Box className={styles.actionRow}>
          {visibleActionButtons.map((key) => {
            const { title: btnTitle, ...btnProps } = actionButtonConfig[key];
            return <UBButton key={key} title={btnTitle} {...btnProps} />;
          })}
        </Box>
      )}
    </Box>
  );

  if (!showPageLayout) {
    return content;
  }

  return (
    <Grid
      container
      className={`${formStyles.root} ${formStyles.SaleExpenseFormWrapper} ${styles.showQrWrapper}`}
    >
      <Grid item md={12}>
        <TitleAndActionBox
          data={{
            title: pageTitle,
            isTitleCase: false,
            isBack: true,
            onBack: handleBack,
            id: "show_qr",
          }}
        />
        {content}
      </Grid>
    </Grid>
  );
};

export default ShowQR;
